import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchCasaMapaAdminListing,
  patchCasaMapaAdminListing,
} from "../../../api/clientCasamapaAdminApi";
import CasaMapaAdminListingAddress from "../../../components/casamapa/admin/CasaMapaAdminListingAddress";
import CasaMapaAdminListingGallery from "../../../components/casamapa/admin/CasaMapaAdminListingGallery";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale, useCurrency } from "../../../intl";
import { formatDetailDateTime } from "../../../utils/propertyDetailFormat";
import { groupListingAttributes, isTimestampAttributeKey } from "../../../utils/casamapaListingAttributeGroups";
import { consolidateListingTimeline, isCommercialProperty } from "../../../utils/casamapaListingTimeline";
import CasaMapaListingRepairDrawer from "./CasaMapaListingRepairDrawer";
import "../../../styles/casamapaAdminPortal.css";
import "../../../styles/casamapaListingHealthDashboard.css";
import "../../../styles/casamapaListingAdminDetail.css";

const COPY = "realEstate.casaMapa.admin.listings";
const DETAIL_COPY = `${COPY}.detail`;

const HIDDEN_CLIENT_ATTRIBUTE_KEYS = new Set([
  "title",
  "media",
  "primaryImage",
  "priceMoney",
  "thumbnailUrl",
  "primaryImageUrl",
  "photoCount",
  "imageCount",
]);

const PRICE_ATTRIBUTE_PATTERN = /(price|fee|amount)/i;
const NON_PRICE_ATTRIBUTE_PATTERN = /(percent|count|score|image|source)/i;

function listingStorageCurrency(detail) {
  return detail?.currency || "COP";
}

function isPriceAttributeKey(key) {
  const normalized = String(key || "");
  return PRICE_ATTRIBUTE_PATTERN.test(normalized) && !NON_PRICE_ATTRIBUTE_PATTERN.test(normalized);
}

function parseAttributeRawValue(value) {
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function formatScalarAttributeValue(key, value, formatMoney, storageCurrency = "COP") {
  if (value == null || value === "") {
    return null;
  }

  if (typeof value === "object") {
    if (value.amount != null && (value.currency || isPriceAttributeKey(key))) {
      const amount = Number(value.amount);
      if (Number.isFinite(amount)) {
        return formatMoney(amount, { storageCurrency: value.currency || storageCurrency });
      }
    }
    const summary = Object.entries(value)
      .filter(([, entryValue]) => entryValue != null && entryValue !== "" && typeof entryValue !== "object")
      .slice(0, 6)
      .map(([entryKey, entryValue]) => `${formatAttributeLabel(entryKey)}: ${entryValue}`)
      .join(" · ");
    return summary || null;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => formatScalarAttributeValue(key, item, formatMoney, storageCurrency))
      .filter(Boolean)
      .join(", ");
  }

  if (isPriceAttributeKey(key)) {
    const amount = Number(value);
    if (Number.isFinite(amount)) {
      return formatMoney(amount, { storageCurrency });
    }
  }

  return String(value);
}

function formatAttributeDisplay(key, rawValue, formatMoney, storageCurrency = "COP") {
  if (rawValue == null || rawValue === "") {
    return null;
  }
  const parsed = parseAttributeRawValue(rawValue);
  if (isTimestampAttributeKey(key) && typeof parsed === "string") {
    const date = new Date(parsed);
    if (!Number.isNaN(date.getTime())) {
      return formatDetailDateTime(parsed);
    }
  }
  return formatScalarAttributeValue(key, parsed, formatMoney, storageCurrency);
}

function formatListingMoney(amount, formatMoney, storageCurrency = "COP") {
  if (amount == null || amount === "") {
    return "—";
  }
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return formatMoney(numeric, { storageCurrency });
}

function listingLocation(detail) {
  const parts = [detail?.neighborhood, detail?.municipality].filter(Boolean);
  return parts.length ? parts.join(", ") : detail?.title || "—";
}

function formatAttributeLabel(key, { commercial = false, roomsLabel = "Rooms" } = {}) {
  if (commercial && /^bedrooms?$/i.test(String(key || ""))) {
    return roomsLabel;
  }
  return String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
}

function formatTimelineDetail(event, formatMoney) {
  const parts = [];
  const storageCurrency = event.currency || "COP";

  const previousPrice = event.previousPriceMin ?? event.previousPrice;
  const nextPrice = event.newPriceMin ?? event.newPrice;
  if (previousPrice != null && nextPrice != null) {
    parts.push(
      `${formatListingMoney(previousPrice, formatMoney, storageCurrency)} → ${formatListingMoney(nextPrice, formatMoney, storageCurrency)}`
    );
  } else if (nextPrice != null) {
    parts.push(formatListingMoney(nextPrice, formatMoney, storageCurrency));
  }

  if (event.priceChangePercent != null) {
    const sign = event.priceChangePercent > 0 ? "+" : "";
    parts.push(`${sign}${event.priceChangePercent}%`);
  }

  if (event.newStatus) {
    parts.push(String(event.newStatus).replace(/_/g, " ").toLowerCase());
  }

  const sources = event.sourceKeys?.length ? event.sourceKeys : event.sourceKey ? [event.sourceKey] : [];
  if (sources.length) {
    parts.push(sources.join(", "));
  }

  return parts.join(" · ");
}

function freshnessTone(status) {
  if (status === "recently_confirmed" || status === "confirmed") return "good";
  if (status === "aging") return "warn";
  if (status === "stale") return "bad";
  return "neutral";
}

function StatChip({ label, value }) {
  if (value == null || value === "" || value === "—") {
    return null;
  }
  return (
    <div className="cm-listing-detail__chip">
      <span className="cm-listing-detail__chip-label">{label}</span>
      <strong className="cm-listing-detail__chip-value">{value}</strong>
    </div>
  );
}

function AttributeGroupCard({
  group,
  title,
  rows,
  formatMoney,
  storageCurrency = "COP",
  commercial = false,
  roomsLabel = "Rooms",
}) {
  const visibleRows = rows
    .filter((row) => !HIDDEN_CLIENT_ATTRIBUTE_KEYS.has(row.key))
    .map((row) => ({
      key: row.key,
      value: formatAttributeDisplay(row.key, row.value, formatMoney, storageCurrency),
    }))
    .filter((row) => row.value);

  if (!visibleRows.length) {
    return null;
  }

  const isIdentifiers = group === "identifiers";
  const isTimestamps = group === "timestamps";

  return (
    <section
      className={`cm-listing-detail__group cm-listing-detail__group--${group}${
        isIdentifiers || isTimestamps ? " cm-listing-detail__group--wide" : ""
      }`}
    >
      <header className="cm-listing-detail__group-head">
        <h2 className="cm-listing-detail__group-title">{title}</h2>
        <span className="cm-listing-detail__group-count">{visibleRows.length}</span>
      </header>

      {isIdentifiers || isTimestamps ? (
        <dl className="cm-listing-detail__id-list">
          {visibleRows.map((row) => (
            <div key={row.key} className="cm-listing-detail__id-row">
              <dt>{formatAttributeLabel(row.key, { commercial, roomsLabel })}</dt>
              <dd className="cm-listing-detail__mono">{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="cm-listing-detail__attr-grid">
          {visibleRows.map((row) => (
            <div key={row.key} className="cm-listing-detail__attr-item">
              <span className="cm-listing-detail__attr-label">
                {formatAttributeLabel(row.key, { commercial, roomsLabel })}
              </span>
              <span className="cm-listing-detail__attr-value">{row.value}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ListingAttributeGroups({ attributes, t, formatMoney, storageCurrency, commercial }) {
  const groups = useMemo(() => groupListingAttributes(attributes), [attributes]);
  const roomsLabel = t(`${DETAIL_COPY}.stats.rooms`);

  if (!groups.length) {
    return null;
  }

  return (
    <div className="cm-listing-detail__groups">
      {groups.map((group) => (
        <AttributeGroupCard
          key={group.id}
          group={group.id}
          title={t(`${DETAIL_COPY}.groups.${group.id}`, {}, formatAttributeLabel(group.id))}
          rows={group.rows}
          formatMoney={formatMoney}
          storageCurrency={storageCurrency}
          commercial={commercial}
          roomsLabel={roomsLabel}
        />
      ))}
    </div>
  );
}

function ListingOverview({
  detail,
  t,
  formatMoney,
  onTogglePublicStreet,
  togglingPublicStreet,
}) {
  const freshness = detail.freshnessStatus || "unknown";
  const completeness =
    detail.completenessScore != null ? `${detail.completenessScore}%` : null;
  const storageCurrency = listingStorageCurrency(detail);

  return (
    <div className="cm-listing-detail__overview">
      <div className="cm-listing-detail__overview-top">
        <p className="cm-listing-detail__price">
          {detail.priceCop != null
            ? formatListingMoney(detail.priceCop, formatMoney, storageCurrency)
            : "—"}
        </p>
        <div className="cm-listing-detail__badges">
          {detail.listingType ? (
            <span className="cm-listing-detail__badge cm-listing-detail__badge--type">
              {t(`realEstate.casaMapa.admin.listingType.${detail.listingType}`)}
            </span>
          ) : null}
          {detail.propertyType ? (
            <span className="cm-listing-detail__badge cm-listing-detail__badge--muted">
              {detail.propertyType}
            </span>
          ) : null}
          <span
            className={`cm-listing-detail__badge cm-listing-detail__badge--freshness cm-listing-detail__badge--${freshnessTone(freshness)}`}
          >
            {t(`${COPY}.freshness.${freshness}`)}
          </span>
        </div>
      </div>

      <p className="cm-listing-detail__location">{listingLocation(detail)}</p>
      <CasaMapaAdminListingAddress
        mailingAddress={detail.mailingAddress}
        t={t}
        onTogglePublicStreet={onTogglePublicStreet}
        togglingPublicStreet={togglingPublicStreet}
      />

      <div className="cm-listing-detail__chips">
        <StatChip
          label={
            isCommercialProperty(detail) ? t(`${DETAIL_COPY}.stats.rooms`) : t(`${DETAIL_COPY}.stats.beds`)
          }
          value={detail.bedrooms != null ? detail.bedrooms : null}
        />
        <StatChip
          label={t(`${DETAIL_COPY}.stats.baths`)}
          value={detail.bathrooms != null ? detail.bathrooms : null}
        />
        <StatChip
          label={t(`${DETAIL_COPY}.stats.area`)}
          value={detail.areaSqm != null ? `${detail.areaSqm} m²` : null}
        />
        <StatChip
          label={t(`${DETAIL_COPY}.stats.parking`)}
          value={detail.parkingSpaces != null ? detail.parkingSpaces : null}
        />
        <StatChip label={t(`${DETAIL_COPY}.summary.completeness`)} value={completeness} />
        <StatChip
          label={t(`${DETAIL_COPY}.stats.photos`)}
          value={detail.images?.length || 0}
        />
        {detail.administrationFee != null ? (
          <StatChip
            label={t(`${DETAIL_COPY}.summary.adminFee`, {}, "Admin fee")}
            value={formatListingMoney(detail.administrationFee, formatMoney, storageCurrency)}
          />
        ) : null}
      </div>

      <dl className="cm-listing-detail__meta-list">
        <div>
          <dt>{t(`${DETAIL_COPY}.summary.agency`)}</dt>
          <dd>{detail.agencyName || detail.sourceKey || "—"}</dd>
        </div>
        <div>
          <dt>{t(`${DETAIL_COPY}.summary.id`)}</dt>
          <dd className="cm-listing-detail__mono">{detail.id}</dd>
        </div>
      </dl>

      {detail.issueIds?.length ? (
        <div className="cm-listing-detail__issues">
          {detail.issueIds.map((issueId) => (
            <span key={issueId} className="cm-listing-health__issue-tag">
              {t(`${COPY}.issues.${issueId}`)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function formatTimelineTime(changedAt) {
  if (!changedAt) return "";
  const date = new Date(changedAt);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function TimelineEvent({ event, t, formatMoney }) {
  const detail = formatTimelineDetail(event, formatMoney);
  const timeLabel = formatTimelineTime(event.changedAt);
  const changeClass =
    event.changeType === "PRICE_DECREASED"
      ? "decrease"
      : event.changeType === "PRICE_INCREASED"
        ? "increase"
        : "neutral";

  return (
    <li>
      <div className="cm-listing-detail__timeline-head">
        <p className={`property-detail-timeline__title property-detail-timeline__title--${changeClass}`}>
          {t(`${DETAIL_COPY}.timeline.changeTypes.${event.changeType}`, {}, event.changeType)}
        </p>
      </div>
      <p className="property-detail-timeline__meta">
        {[timeLabel, detail].filter(Boolean).join(" · ")}
      </p>
    </li>
  );
}

function ListingTimeline({ events = [], t, formatMoney }) {
  const [openDays, setOpenDays] = useState(() => new Set());
  const groups = useMemo(() => consolidateListingTimeline(events), [events]);

  if (!events.length) {
    return (
      <section className="cm-listing-detail__section-card">
        <h2 className="cm-listing-detail__section-title">{t(`${DETAIL_COPY}.timeline.title`)}</h2>
        <p className="muted">{t(`${DETAIL_COPY}.timeline.empty`)}</p>
      </section>
    );
  }

  return (
    <section className="cm-listing-detail__section-card">
      <h2 className="cm-listing-detail__section-title">{t(`${DETAIL_COPY}.timeline.title`)}</h2>
      <p className="muted">{t(`${DETAIL_COPY}.timeline.hint`)}</p>
      <ol className="cm-listing-detail__timeline-days">
        {groups.map((group) => {
          const open = openDays.has(group.date);
          const dateLabel =
            group.date === "unknown"
              ? t(`${DETAIL_COPY}.timeline.unknownDate`)
              : new Date(`${group.date}T00:00:00`).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                });
          return (
            <li key={group.date} className="cm-listing-detail__timeline-day">
              <h3>{dateLabel}</h3>
              <ol className="cm-listing-detail__timeline-events">
                {group.events.map((event, index) => (
                  <TimelineEvent
                    key={`${event.id || event.changedAt}-${event.changeType}-${index}`}
                    event={event}
                    t={t}
                    formatMoney={formatMoney}
                  />
                ))}
              </ol>
              {group.extraCount ? (
                <>
                  <button
                    type="button"
                    className="casamapa-admin-link-button"
                    onClick={() =>
                      setOpenDays((current) => {
                        const next = new Set(current);
                        if (next.has(group.date)) next.delete(group.date);
                        else next.add(group.date);
                        return next;
                      })
                    }
                  >
                    {open
                      ? t(`${DETAIL_COPY}.timeline.hideExtra`)
                      : t(`${DETAIL_COPY}.timeline.moreThatDay`, { count: group.extraCount })}
                  </button>
                  {open ? (
                    <ol className="cm-listing-detail__timeline-events cm-listing-detail__timeline-events--extra">
                      {group.extras.map((event, index) => (
                        <TimelineEvent
                          key={`${event.id || event.changedAt}-${event.changeType}-extra-${index}`}
                          event={event}
                          t={t}
                          formatMoney={formatMoney}
                        />
                      ))}
                    </ol>
                  ) : null}
                </>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function normalizeListingDetailPayload(payload) {
  if (!payload) {
    return { detail: null, timeline: [], history: null };
  }

  if (payload.detail) {
    return {
      detail: payload.detail,
      timeline: payload.timeline || [],
      history: payload.history || null,
    };
  }

  const listing = payload.listing;
  if (!listing) {
    return { detail: null, timeline: [], history: null };
  }

  const images = listing.photoUrl
    ? [{ id: "primary", url: listing.photoUrl, thumbnailUrl: listing.photoUrl, isPrimary: true }]
    : [];

  return {
    detail: {
      ...listing,
      images,
      attributes: {},
      publicDetail: listing,
    },
    timeline: payload.timeline || [],
    history: payload.history || null,
  };
}

export default function CasaMapaAdminListingDetailPage() {
  const { listingId } = useParams();
  const { path, clientViewId } = useCasaMapaAdminPortal();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [repairOpen, setRepairOpen] = useState(false);
  const [togglingPublicStreet, setTogglingPublicStreet] = useState(false);

  const loadListing = useCallback(async () => {
    if (!clientViewId || !listingId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const result = await fetchCasaMapaAdminListing(clientViewId, listingId);
      setPayload(result || null);
    } catch (err) {
      setError(err.message || t(`${DETAIL_COPY}.loadError`));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, listingId, t]);

  useEffect(() => {
    loadListing();
  }, [loadListing]);

  const handleTogglePublicStreet = useCallback(
    async (nextApproved) => {
      if (!clientViewId || !listingId) return;
      setTogglingPublicStreet(true);
      try {
        const result = await patchCasaMapaAdminListing(clientViewId, listingId, {
          publicStreetApproved: nextApproved,
        });
        if (result?.listing) {
          setPayload((current) => ({
            ...(current || {}),
            listing: {
              ...(current?.listing || {}),
              ...result.listing,
              mailingAddress:
                result.listing.mailingAddress || current?.listing?.mailingAddress || null,
            },
          }));
        } else {
          await loadListing();
        }
      } finally {
        setTogglingPublicStreet(false);
      }
    },
    [clientViewId, listingId, loadListing]
  );

  const { detail, timeline } = useMemo(() => normalizeListingDetailPayload(payload), [payload]);
  const commercial = isCommercialProperty(detail);

  if (!clientViewId) {
    return <p className="muted">Select a CasaMapa client view to continue.</p>;
  }

  return (
    <section className="casamapa-admin-page cm-listing-detail">
      <header className="casamapa-admin-page__header cm-listing-detail__header">
        <div>
          <div className="eyebrow">{t("realEstate.casaMapa.admin.portalEyebrow")}</div>
          <h1>{detail?.title || t(`${DETAIL_COPY}.title`)}</h1>
          {detail ? (
            <p className="muted cm-listing-detail__header-sub">
              {listingLocation(detail)}
              {detail.priceCop != null
                ? ` · ${formatListingMoney(detail.priceCop, formatMoney, listingStorageCurrency(detail))}`
                : ""}
            </p>
          ) : (
            <p className="muted">{t(`${DETAIL_COPY}.subtitle`)}</p>
          )}
        </div>
        <div className="cm-listing-detail__header-actions">
          <Link className="casamapa-admin-link-button" to={path("listings")}>
            {t(`${DETAIL_COPY}.backToList`)}
          </Link>
          <Link className="casamapa-admin-link-button" to={path(`listings/${listingId}/data-health`)}>
            {t(`${DETAIL_COPY}.dataHealthLink`)}
          </Link>
          {detail?.publicUrl ? (
            <a className="casamapa-admin-link-button" href={detail.publicUrl} target="_blank" rel="noreferrer">
              {t("realEstate.casaMapa.admin.viewPublic")}
            </a>
          ) : null}
          {detail ? (
            <button type="button" className="cm-listing-health__fix-button" onClick={() => setRepairOpen(true)}>
              {t(`${COPY}.repairAction`)}
            </button>
          ) : null}
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {loading ? <p className="muted">{t(`${DETAIL_COPY}.loading`)}</p> : null}

      {!loading && !detail && !error ? (
        <p className="muted">{t(`${DETAIL_COPY}.loadError`)}</p>
      ) : null}

      {!loading && detail ? (
        <>
          <div className="cm-listing-detail__top">
            <CasaMapaAdminListingGallery images={detail.images || []} t={t} />
            <ListingOverview
              detail={detail}
              t={t}
              formatMoney={formatMoney}
              onTogglePublicStreet={handleTogglePublicStreet}
              togglingPublicStreet={togglingPublicStreet}
            />
          </div>

          {detail.description ? (
            <section className="cm-listing-detail__section-card cm-listing-detail__description-card">
              <h2 className="cm-listing-detail__section-title">{t(`${DETAIL_COPY}.summary.description`)}</h2>
              <p className="cm-listing-detail__description-text">{detail.description}</p>
            </section>
          ) : null}

          <div className="cm-listing-detail__body">
            <div className="cm-listing-detail__sections">
              <ListingAttributeGroups
                attributes={detail.attributes}
                t={t}
                formatMoney={formatMoney}
                storageCurrency={listingStorageCurrency(detail)}
                commercial={commercial}
              />
            </div>

            <aside className="cm-listing-detail__aside">
              <ListingTimeline events={timeline} t={t} formatMoney={formatMoney} />
            </aside>
          </div>
        </>
      ) : null}

      {repairOpen && listingId ? (
        <CasaMapaListingRepairDrawer
          clientViewId={clientViewId}
          listingId={listingId}
          onClose={() => setRepairOpen(false)}
          onSaved={() => {
            setRepairOpen(false);
            loadListing();
          }}
        />
      ) : null}
    </section>
  );
}
